import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Row, Col } from 'reactstrap';
import Card from 'shared/components/Card/Card';
import styles from './InvoicesStatus.styles';
import { Currency } from 'shared/components/Transactions/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faExclamationCircle, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import sctheme from 'app/styles/sctheme';
import { formatNumberWithFixedDecimals } from 'app/Portal/Billing/utilities/UtilityFunctions';

const InvoicingBusy = ({ isBusy, children }) => {
  if (isBusy) return <FontAwesomeIcon icon={faArrowsRotate} className="invoicing-busy" />;
  return children;
};

class InvoicesStatus extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      totalAllocationAmount: null,
      totalUtilityCredit: null,
      totalAllocationCost: null
    };
  }

  aggregateBillingDetails(subscriberData) {
    this.setState({ loading: true });
    let totalAllocationAmount = 0;
    let totalUtilityCredit = 0;
    let totalAllocationCost = 0;

    subscriberData.forEach(subscriber => {
      if (subscriber.projectDetails && Array.isArray(subscriber.projectDetails)) {
        subscriber.projectDetails.forEach(project => {
          totalAllocationAmount += project.allocationAmount || 0;
          totalUtilityCredit += project.utilityCredit || 0;
          totalAllocationCost += project.allocationCost || 0;
        });
      }
    });

    this.setState({
      totalAllocationAmount,
      totalUtilityCredit,
      totalAllocationCost,
      loading: false
    });
  }

  componentDidMount() {
    if (this.props.subscriberData) {
      this.aggregateBillingDetails(this.props.subscriberData);
    } else {
      this.setState({ loading: false });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.subscriberData !== this.props.subscriberData) {
      this.aggregateBillingDetails(this.props.subscriberData);
    }
  }

  render() {
    const {
      isInvoicingBusy,
      name,
      successfullySentCount,
      successfullyGeneratedInvoiceCount,
      failedToGenerateInvoiceCount,
      filters,
      totalInvoiceCount,
      canGenerate,
      canFinalize,
      subscriberData,
      invoicedBeforeLaunchDate
    } = this.props;

    const showTotal =
      !invoicedBeforeLaunchDate &&
      !canGenerate &&
      (!canFinalize || failedToGenerateInvoiceCount === 0) &&
      totalInvoiceCount !== 0;

    return (
      <div css={styles}>
        {invoicedBeforeLaunchDate && (
          <div
            className="d-flex p-3 mb-3"
            style={{
              backgroundColor: sctheme.system.infoLight,
              border: `1px solid ${sctheme.system.info}`,
              borderRadius: '4px'
            }}
          >
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="mr-2"
              style={{ marginTop: '4px', color: sctheme.system.info }}
            />
            <div>
              This batch was created before the invoice by project feature. Subscriber project allocations and invoice
              totals will not show on this page, but can be accessed in the Invoice Detail Report.
            </div>
          </div>
        )}
        {totalInvoiceCount > successfullyGeneratedInvoiceCount && canFinalize ? (
          <div
            className="d-flex p-3 mb-3"
            style={{
              backgroundColor: sctheme.system.errorLight,
              border: `1px solid ${sctheme.system.error}`,
              borderRadius: '4px'
            }}
          >
            <FontAwesomeIcon
              icon={faExclamationCircle}
              className="mr-2"
              style={{ marginTop: '4px', color: sctheme.system.error }}
            />
            <div>
              Some invoices were not created. Please use the Preflight Check to identify errors, or delete the
              subscribers from the batch.
            </div>
          </div>
        ) : null}
        <div className="status-label">Batch</div>
        <div className="status-value">{name}</div>
        <Row className="mt-4">
          <Col>
            <Card
              className="d-flex justify-content-between"
              style={{
                padding: '10px 20px',
                gap: '4px'
              }}
            >
              <div className="status">
                <div className="status-label">
                  Total <br /> Subscribers
                </div>
                <div className="status-value">{subscriberData.length}</div>
              </div>
              <div className="status">
                <div className="status-label">
                  Total Statements <br /> Generated
                </div>
                <InvoicingBusy isBusy={isInvoicingBusy}>
                  <div className="status-value">
                    {(!canGenerate || failedToGenerateInvoiceCount > 0) && totalInvoiceCount !== 0
                      ? successfullyGeneratedInvoiceCount
                      : '--'}
                  </div>
                </InvoicingBusy>
              </div>
              <div className="status">
                <div className="status-label">
                  Total Statements <br /> Sent
                </div>
                <InvoicingBusy isBusy={isInvoicingBusy}>
                  <div className="status-value">
                    {!canFinalize && !canGenerate && totalInvoiceCount !== 0 ? successfullySentCount : '--'}
                  </div>
                </InvoicingBusy>
              </div>
              <div className="status">
                <div className="status-label">
                  Total <br /> Invoices
                </div>
                <InvoicingBusy isBusy={isInvoicingBusy}>
                  <div className="status-value">
                    {showTotal ? <Currency amount={this.state.totalAllocationCost} /> : '--'}
                  </div>
                </InvoicingBusy>
              </div>
              <div className="status">
                <div className="status-label">
                  Total Utility <br /> Credits
                </div>
                <InvoicingBusy isBusy={isInvoicingBusy}>
                  <div className="status-value">
                    {showTotal ? <Currency amount={this.state.totalUtilityCredit} /> : '--'}
                  </div>
                </InvoicingBusy>
              </div>
              <div className="status">
                <div className="status-label">
                  Total <br /> Allocation <span style={{ textTransform: 'none' }}>(kWh)</span>
                </div>
                <InvoicingBusy isBusy={isInvoicingBusy}>
                  <div className="status-value">{showTotal ? formatNumberWithFixedDecimals(this.state.totalAllocationAmount) : '--'}</div>
                </InvoicingBusy>
              </div>
            </Card>
          </Col>
        </Row>
        {filters?.length > 0 ? (
          <Row className="mt-3">
            <Col className="d-flex">
              <div className="mr-3 font-weight-bold filter-title">Batch Filters:</div>
              <div className="d-flex filter-results-wrapper">
                {filters.map(filter => {
                  return (
                    <div className="filter" key={filter}>
                      {filter}
                    </div>
                  );
                })}
              </div>
            </Col>
          </Row>
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  isInvoicingBusy: state.invoices.isInvoicingBusy
});

export default connect(mapStateToProps)(InvoicesStatus);
